import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { apiErrorDetail } from '../../lib/apiError'
import { CacheKeys } from '../../lib/cache'
import { dispatchBlockedSecondsAgo, resubmitIndications } from '../../models/insuranceApplication'
import type { CoverageType } from '../../models/insuranceCoverage'
import { Vendor } from '../../models/insuranceTypes'

export enum ResubmitLabel {
  RESUBMIT = 'Resubmit',
  SUBMIT = 'Submit',
}

function copyFor(label: ResubmitLabel, vendorName: string) {
  if (label === ResubmitLabel.SUBMIT) {
    return {
      title: `Submit to ${vendorName}`,
      description: `Run ${vendorName} using the application's latest onboarding details. Continue?`,
      loading: 'Submitting…',
      success: `Submitted to ${vendorName}`,
      error: `Failed to submit to ${vendorName}`,
    }
  }
  return {
    title: 'Resubmit indication',
    description: `Re-run ${vendorName} using the application's latest onboarding details. Continue?`,
    loading: 'Resubmitting…',
    success: 'Resubmitted with the latest application details',
    error: 'Failed to resubmit indication',
  }
}

/** Re-run a vendor's indication against the application's current onboarding data. */
export function ResubmitIndicationButton({
  vendor,
  vendorName,
  applicationId,
  orgId,
  label = ResubmitLabel.RESUBMIT,
  coverageLine,
  disabledReason,
  emphasized,
}: {
  vendor: Vendor
  vendorName: string
  applicationId: string
  /** The staff Insurance tab reads the org's application query, so it is refreshed too. */
  orgId?: string
  label?: ResubmitLabel
  /** Rocklake only: send this one line rather than every Rocklake line. */
  coverageLine?: CoverageType
  /** Why a resubmit can't run now. Disables the button and shows as its tooltip. */
  disabledReason?: string | null
  /** A resubmit would send changes the carrier hasn't seen, so the button stands out. */
  emphasized?: boolean
}) {
  const queryClient = useQueryClient()
  const [isResubmitting, setIsResubmitting] = useState(false)
  // Set when the backend refused a fast repeat; opens the second confirmation.
  const [blockedSecondsAgo, setBlockedSecondsAgo] = useState<number | null>(null)
  const copy = copyFor(label, vendorName)

  async function resubmit(force: boolean) {
    setIsResubmitting(true)
    try {
      await resubmitIndications(applicationId, vendor, force, coverageLine)
      await queryClient.invalidateQueries({ queryKey: [CacheKeys.APPLICATION, applicationId] })
      if (orgId) {
        // The prefix also refreshes the org itself, whose summary drives the older-version banner.
        await queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, orgId] })
      }
      toast.success(copy.success)
    } catch (error) {
      const secondsAgo = dispatchBlockedSecondsAgo(error)
      if (secondsAgo !== null) {
        setBlockedSecondsAgo(secondsAgo)
        return
      }
      // The backend's refusal names the reason, such as a quote the carrier already holds.
      toast.error(apiErrorDetail(error, copy.error))
    } finally {
      setIsResubmitting(false)
    }
  }

  if (disabledReason) {
    return (
      <Tooltip>
        {/* A disabled button gets no pointer events, so the wrapper carries the tooltip. */}
        <TooltipTrigger asChild>
          <span tabIndex={0}>
            <Button size="sm" variant="outline" disabled>
              {label}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{disabledReason}</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <>
      <ConfirmationDialog
        title={copy.title}
        description={copy.description}
        confirmLabel={label}
        onConfirm={() => resubmit(false)}
      >
        <Button
          size="sm"
          variant={emphasized ? 'default' : 'outline'}
          disabled={isResubmitting}
          loading={isResubmitting ? copy.loading : undefined}
        >
          {label}
        </Button>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={blockedSecondsAgo !== null}
        onOpenChange={(open) => {
          if (!open) setBlockedSecondsAgo(null)
        }}
        title="Send again?"
        description={`${vendorName} was sent ${blockedSecondsAgo ?? 0} seconds ago. Sending again creates another quote in the carrier's portal.`}
        confirmLabel="Send again"
        onConfirm={() => resubmit(true)}
      />
    </>
  )
}
